import { useState, useEffect, useRef } from "react";
import Api from "../../../../shared/api/api";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-resize-module";
import "quill-resize-module/dist/resize.css";
import "react-quill/dist/quill.snow.css";
import "../styles/AdminTestimonials.css";
import "../styles/AdminNewsletter.css";
import { DateTime } from "luxon";

Quill.register("modules/imageResize", ImageResize);

function convertToUtc(localDatetime) {
  if (!localDatetime) return null;
  return DateTime.fromISO(localDatetime, { zone: "America/Los_Angeles" })
    .toUTC()
    .toISO({ suppressMilliseconds: true });
}

export default function Newsletter() {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("");

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const [scheduledAt, setScheduledAt] = useState("");

  const quillRef = useRef(null);
  const campaignRequest = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  /* -------------------------------------------
     EMOJI SYSTEM (copied from Blogs.js)
  ------------------------------------------- */
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("classic");

  const emojiCategories = {
    classic: ["😀","😃","😄","😁","😆","😅","😂","🤣","🙂","😉"],
    expressive: ["😊","😇","😍","🤩","😘","😎","🤔","😐","😶","🙄","😏","🥺"],
    dramatic: ["😤","😠","😡","😢","😭","😱","😨","😓","😥"],
    celebration: ["🔥","✨","💫","⚡","🎉","🎊","🏆","🌟","🌙","⭐"],
    writing: ["📚","✍️","📝","✒️","🎧","🕰️","🧭","🧩","🔍"],
    fantasy: ["🐉","🗡️","🛡️","🔮","🧙‍♂️","🧝‍♂️","🧛‍♂️","👁️"],
    utility: ["📸","🎬","🎵","💡","💬","📣","👀","🌄"],
  };

  const getFilteredEmojis = () => {
    let list = emojiCategories[activeCategory];
    if (!emojiSearch.trim()) return list;
    return list.filter((em) =>
      em.toLowerCase().includes(emojiSearch.toLowerCase())
    );
  };

  const insertEmojiAtCursor = (emoji) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    if (range) {
      editor.insertText(range.index, emoji);
      editor.setSelection(range.index + emoji.length);
      setContent(editor.root.innerHTML);
    }
  };

  /* -------------------------------------------
     LOAD TEMPLATES
  ------------------------------------------- */
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await Api.getNewsletterTemplates();
      setTemplates(res || []);
    } catch (err) {
      console.error("Template load error:", err);
    }
  };

  const handleApplyTemplate = () => {
    if (!selectedTemplate) return;
    const tpl = templates.find((t) => t.id == selectedTemplate);
    if (!tpl) return;
    setContent((prev) => prev + tpl.body_html);
  };

  /* -------------------------------------------
     SEND NOW
  ------------------------------------------- */
  const handleSendNow = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (campaignRequest.current && (campaignRequest.current.subject !== subject || campaignRequest.current.content !== content)) {
      setStatus("An earlier request is unresolved. Check Email History before starting a different campaign.");
      return;
    }
    if (!campaignRequest.current) campaignRequest.current = { subject, content, request_id: window.crypto.randomUUID() };
    setSubmitting(true);
    setStatus("");

    try {
      const res = await Api.sendNewsletter(campaignRequest.current);
      setStatus(res.message || "Newsletter sent!");
      setSubject("");
      setContent("");
      campaignRequest.current = null;
    } catch (err) {
      if (err.status === 400) campaignRequest.current = null;
      setStatus(err.message || "Submission not confirmed. Retry the same draft, or check Email History.");
    } finally { setSubmitting(false); }
  };

  /* -------------------------------------------
     TEST SEND
  ------------------------------------------- */
  const handleTestSend = async () => {
    if (!testEmail) {
      setStatus("Enter a test email first.");
      return;
    }

    try {
      const res = await Api.sendTestNewsletter({
        to: testEmail,
        subject,
        content,
      });
      setStatus(res.message || "Test email sent!");
    } catch (err) {
      setStatus(err?.response?.data?.message || "Test send failed.");
    }
  };

  /* -------------------------------------------
     SCHEDULE NEWSLETTER
  ------------------------------------------- */
  const handleSchedule = async () => {
    setStatus("");

    if (!scheduledAt) {
      setStatus("Please select a schedule time.");
      return;
    }

    try {
      const res = await Api.scheduleNewsletter({
        subject,
        body_html: content,
        scheduled_at: convertToUtc(scheduledAt),
      });

      setStatus(res.message || "Newsletter scheduled!");
      setSubject("");
      setContent("");
      setScheduledAt("");
    } catch (err) {
      setStatus(err?.response?.data?.message || "Schedule failed.");
    }
  };

  return (
    <div className="adminPageContainer">
      <div className="newsletterCard">
        <h1 className="text-2xl font-bold mb-4">Send Newsletter</h1>

        {/* TEMPLATE CONTROLS */}
        <div className="templateRow">
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
          >
            <option value="">Select a Footer Template...</option>
            {templates
              .filter((tpl) =>
                tpl.template_name?.toLowerCase().includes("newsletter")
              )
              .map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.template_name}
                </option>
            ))}

          </select>

          <button
            className="insertFooterBtn"
            onClick={handleApplyTemplate}
            disabled={!selectedTemplate}
          >
            Insert Footer
          </button>
        </div>

        {/* MAIN FORM */}
        <form className="testimonialForm" onSubmit={handleSendNow}>
          <input
            type="text"
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />

          {/* RICH TEXT EDITOR WITH EMOJIS */}
          <div className="richTextEditorWrapper">
            <button
              type="button"
              className="emojiToggleBtn"
              onClick={() => setShowEmojiPicker((prev) => !prev)}
            >
              😀 Emoji
            </button>

            {showEmojiPicker && (
              <div className="emojiPickerContainer">
                <input
                  type="text"
                  className="emojiSearchInput"
                  placeholder="Search emoji..."
                  value={emojiSearch}
                  onChange={(e) => setEmojiSearch(e.target.value)}
                />

                <div className="emojiCategoryTabs">
                  {Object.keys(emojiCategories).map((cat) => (
                    <div
                      key={cat}
                      className="emojiCategoryTab"
                      style={{
                        background:
                          activeCategory === cat ? "#e3d9f8" : "#f1ecfa",
                      }}
                      onClick={() => setActiveCategory(cat)}
                    >
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </div>
                  ))}
                </div>

                <div className="emojiGrid">
                  {getFilteredEmojis().map((em, i) => (
                    <button
                      key={`${activeCategory}_${i}`}
                      type="button"
                      className="emojiBtn"
                      onClick={() => insertEmojiAtCursor(em)}
                    >
                      {em}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <ReactQuill
              ref={quillRef}
              value={content}
              onChange={setContent}
              modules={{
                toolbar: [
                  [{ header: [1, 2, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  [{ align: [] }], 
                  ["link", "image"],
                  ["clean"],
                ],
                imageResize: { modules: ["Resize", "DisplaySize"] },
              }}
              formats={[
                "header",
                "bold",
                "italic",
                "underline",
                "strike",
                "list",
                "bullet",
                "align",
                "link",
                "image",
              ]}
              placeholder="Write your newsletter..."
              style={{ height: "300px", marginBottom: "1rem" }}
            />
          </div>

          {/* TEST SEND */}
          <div className="testRow">
            <input
              type="email"
              placeholder="Send test to..."
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
            <button type="button" className="sendTestBtn" onClick={handleTestSend}>
              Send Test
            </button>
          </div>

          {/* SCHEDULE INPUT */}
          <div className="scheduleRow">
            <label className="scheduleValue">Schedule Send</label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
          </div>

          {/* SEND + SCHEDULE BUTTONS */}
          <div className="buttonRow">
            <button type="submit" className="publishingSubmitBtn" disabled={submitting}>
              {submitting ? "Queueing..." : "Send Now"}
            </button>

            <button
              type="button"
              className="publishingSubmitBtn"
              onClick={handleSchedule}
              disabled={submitting || Boolean(campaignRequest.current)}
            >
              Schedule Newsletter
            </button>
          </div>

          {status && (
            <p style={{ textAlign: "center", marginTop: "1rem", fontWeight: "bold" }}>
              {status}
            </p>
          )}
        </form>

        {/* PREVIEW MODAL */}
        {showPreview && (
          <div className="previewModal">
            <div className="previewContent">
              <h2>{subject}</h2>
              <div dangerouslySetInnerHTML={{ __html: content }}></div>
              <button className="secondaryBtn" onClick={() => setShowPreview(false)}>
                Close Preview
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
